import { Box, Button, Drawer, drawerClasses, styled } from "~/common/mui";
import classNames from "classnames";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import type { AddToCartResponseItem } from "~/features/cart/cart/api/AddToCart";
import { LinkButton } from "~/common/components/Link";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import type { ProductData } from "~/domains/product/types/ProductData";
import type { RootState } from "~/app/store";
import { selectIsInFlight } from "~/features/search/redux/searchSelectors";
import classes from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal.module.less";
import { useMultipleRelatedProducts } from "~/domains/product/redux/relatedProducts/useRelatedProducts";
import { resolveCartPageUrl } from "~/utils/url/urlUtils";
import { useMultipleTopsellerProducts } from "~/domains/product/redux/topsellerProducts/useTopsellerProducts";
import { Recommendations } from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/components/CartSuccessRecommendations/Recommendations";
import type { PurchasePlace } from "~/app/analytics/ga/recommendations/types";
import { RecommendationsSkeleton } from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/components/CartSuccessRecommendations/RecommendationsSkeleton";
import AddToCartResult from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/components/AddToCartResult";
import {
  type AddToCartResultFormData,
  shouldRenderMultipleEntriesResult,
} from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/components/addToCartResultUtils";

interface CartSuccessModalProps {
  onClose: () => void;
  formData: AddToCartResultFormData;
  linearConfig?: ProductData;
  className?: string;
  page: PurchasePlace;
  responseItems?: AddToCartResponseItem[];
}
const CartSuccessModal = ({
  onClose,
  formData,
  linearConfig,
  className,
  page,
  responseItems,
}: CartSuccessModalProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();

  const inFlight = useSelector(selectIsInFlight);

  if (inFlight) {
    onClose();
  }

  const productCode = formData[0].productCode;

  const productDescriptor = ProductService.useProductDescriptor({
    productCode: productCode,
    priceReference: getInitialPriceReference(),
    options: { fetch: true },
  });

  //we only need product info if it's for single entries

  const maxFetchedRelatedProducts = 5;

  const getProductBasicInfo = (state: RootState) => state?.products.basic[productCode];

  const productBasicInfoFromStore = useSelector(getProductBasicInfo);
  const productBasicInfo = linearConfig ?? productBasicInfoFromStore;

  const { relatedProducts, state: relatedState } = useMultipleRelatedProducts(
    formData.map((entry) => entry.productCode).slice(0, maxFetchedRelatedProducts),
  );

  const { topsellerProducts, state: topsellerState } = useMultipleTopsellerProducts(
    formData.map((entry) => entry.productCode).slice(0, maxFetchedRelatedProducts),
  );

  const hasMultiple = shouldRenderMultipleEntriesResult({
    formData,
    productInfo: productBasicInfo,
    responseItems,
    phaseOut2Enabled: isPhaseOut2Enabled(),
  });

  // We only show related products on the CartSuccessModal if the product's line is whitelisted
  const visibleRelatedProducts = relatedProducts.filter((product) => product.showPostCartModal);

  let recommendations;
  if (relatedState === "loading" || topsellerState === "loading") {
    recommendations = (
      <>
        <RecommendationsSkeleton title={t("addToCart.relatedProducts")} />
        <RecommendationsSkeleton title={t("addToCart.topsellerProducts")} />
      </>
    );
  } else {
    recommendations = (
      <>
        <Recommendations
          entries={visibleRelatedProducts}
          title={t("addToCart.relatedProducts")}
          tracking-aux-reco-type="rsp"
          place={page}
          tracking-aux-title={t("addToCart.relatedProducts", { lng: "en" })}
          tracking-aux-location="add-to-cart-pop-up"
        />
        <Recommendations
          entries={topsellerProducts}
          title={t("addToCart.topsellerProducts")}
          tracking-aux-reco-type="top"
          place={page}
          tracking-aux-title={t("addToCart.topsellerProducts", { lng: "en" })}
          tracking-aux-location="add-to-cart-pop-up"
        />
      </>
    );
  }

  return (
    <StyledDrawer
      anchor={isDesktop ? "right" : "bottom"}
      className={className}
      sx={{
        "& .MuiAlert-standard": {
          width: "100%",
          alignSelf: "center",
        },
      }}
      open
      onClose={onClose}
    >
      <Box
        sx={{
          padding: "16px",
          display: "flex",
          justifyContent: "end",
          borderBottom: !isTablet ? `1px solid ${C_OUTLINE_VARIANT}` : "",
        }}
      >
        <LinkButton type="button" onClick={onClose}>
          <Icon type={IconType.Close} />
        </LinkButton>
      </Box>
      <Box
        sx={{
          flex: isTablet ? "0" : "",
          overflowY: "visible",
          padding: "0",
        }}
      >
        <div className={classes.dialog}>
          <AddToCartResult
            productInfo={productBasicInfo}
            formData={formData}
            isTablet={isTablet}
            isLinear={!!linearConfig}
            responseItems={responseItems}
          />
          {!isTablet && <div className={classes.recommendations}>{recommendations}</div>}
        </div>
      </Box>
      {productDescriptor.base && (
        <Box
          className={classNames(classes.actions, hasMultiple && classes.multiple)}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr 2fr 1fr",
            columnGap: isDesktop ? "16px" : isTablet ? "24px" : "8px",
            gridTemplateAreas: isDesktop
              ? `"continue continue cart cart"`
              : isTablet
              ? `". continue cart ."`
              : `"continue continue cart cart"`,
            padding: isTablet ? "16px 24px 0" : "16px",
            borderTop: !isTablet ? `1px solid ${C_OUTLINE_VARIANT}` : "none",
          }}
        >
          <Button
            color={isTablet ? "secondary" : "primary"}
            className={classes.continue}
            variant={isTablet ? "contained" : "text"}
            fullWidth
            onClick={onClose}
          >
            {t("notFound.continue")}
          </Button>
          <Button
            href={resolveCartPageUrl()}
            className={classes.cart}
            startIcon={<Icon type={IconType.Cart} />}
            fullWidth
          >
            {t("addToCartPopup.checkoutBtn")}
          </Button>
        </Box>
      )}
      {isTablet && <div className={classes.tabletContainer}>{recommendations}</div>}
    </StyledDrawer>
  );
};

export default CartSuccessModal;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    width: "100wv",
    height: "100dvh",
    [theme.breakpoints.up("desktop-s")]: {
      width: "472px",
    },
    [theme.breakpoints.up("desktop-m")]: {
      width: "528px",
    },
    [theme.breakpoints.up("desktop-l")]: {
      width: "672px",
    },
  },
}));
